<template>
  <div class="login-container">
    <div class="login-card">
      <h2>{{ isRegistering ? 'Registrera dig' : 'Logga in' }}</h2>
      
      <form @submit.prevent="handleSubmit" class="login-form">
        <div class="form-group">
          <label for="email">E-post:</label>
          <input
            type="email"
            id="email"
            v-model="form.email"
            required
            :disabled="loading"
            placeholder="din@email.se"
          />
        </div>

        <div v-if="isRegistering" class="form-group">
          <label for="name">Namn:</label>
          <input
            type="text"
            id="name"
            v-model="form.name"
            required
            :disabled="loading"
            placeholder="Ditt fullständiga namn"
          />
        </div>
        
        <div class="form-group">
          <label for="password">Lösenord:</label>
          <input
            type="password"
            id="password"
            v-model="form.password"
            required
            :disabled="loading"
            placeholder="Minst 8 tecken"
            minlength="8"
          />
        </div>

        <div v-if="isRegistering" class="form-group">
          <label for="captcha">Skriv svaret på uträkningen:</label>
          <div class="captcha-row">
            <img
              v-if="captchaImage"
              :src="captchaSrc"
              alt="captcha"
              class="captcha-img"
            />
            <button type="button" class="captcha-refresh" @click="loadCaptcha" :disabled="loading" title="Ny bild">↻</button>
          </div>
          <input
            type="text"
            id="captcha"
            v-model="form.captchaAnswer"
            required
            :disabled="loading"
            inputmode="numeric"
            autocomplete="off"
            placeholder="Ditt svar"
          />
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
          <button
            v-if="showResend"
            type="button"
            class="resend-link"
            @click="resend"
            :disabled="loading"
          >Skicka verifieringslänken igen</button>
        </div>

        <div v-if="successMessage" class="success-message">
          {{ successMessage }}
        </div>

        <button type="submit" :disabled="loading" class="submit-btn">
          <span v-if="loading">⏳</span>
          <span v-else>{{ isRegistering ? 'Registrera' : 'Logga in' }}</span>
        </button>
      </form>
      
      <div class="switch-mode">
        <p v-if="!isRegistering">
          Har du inget konto? 
          <a href="#" @click.prevent="toggleMode" class="switch-link">Registrera dig här</a>
        </p>
        <p v-else>
          Har du redan ett konto? 
          <a href="#" @click.prevent="toggleMode" class="switch-link">Logga in här</a>
        </p>
      </div>
      
      <button @click="$emit('close')" class="close-btn" :disabled="loading">
        Stäng
      </button>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api.js';
import authService from '@/services/auth.js';

export default {
  name: 'LoginComponent',
  emits: ['loginSuccess', 'close'],
  props: {
    startInRegister: { type: Boolean, default: false }
  },
  data() {
    return {
      isRegistering: this.startInRegister,
      loading: false,
      error: null,
      successMessage: null,
      showResend: false,
      captchaId: null,
      captchaImage: null,
      form: {
        email: '',
        password: '',
        name: '',
        captchaAnswer: ''
      }
    };
  },
  computed: {
    captchaSrc() {
      return this.captchaImage
        ? 'data:image/svg+xml;utf8,' + encodeURIComponent(this.captchaImage)
        : '';
    }
  },
  mounted() {
    if (this.isRegistering) this.loadCaptcha();
  },
  methods: {
    async loadCaptcha() {
      this.form.captchaAnswer = '';
      try {
        const c = await apiService.getCaptcha();
        this.captchaId = c.id;
        this.captchaImage = c.image;
      } catch (e) {
        this.captchaImage = null;
      }
    },

    toggleMode() {
      this.isRegistering = !this.isRegistering;
      this.error = null;
      this.successMessage = null;
      this.showResend = false;
      if (this.isRegistering) this.loadCaptcha();
    },

    async handleSubmit() {
      this.loading = true;
      this.error = null;
      this.successMessage = null;
      this.showResend = false;

      try {
        if (this.isRegistering) {
          await this.register();
        } else {
          await this.login();
        }
      } catch (error) {
        this.error = error.message || 'Ett fel uppstod';
        // Erbjud "skicka igen" om kontot inte är verifierat
        if (/verifiera din e-post/i.test(this.error)) this.showResend = true;
        // Captchan är förbrukad vid registreringsförsök – ladda en ny
        if (this.isRegistering) this.loadCaptcha();
      } finally {
        this.loading = false;
      }
    },

    async register() {
      const result = await apiService.registerUser(
        this.form.email, this.form.password, this.form.name,
        this.captchaId, this.form.captchaAnswer
      );
      this.successMessage = result.message || 'Konto skapat! Kolla din e-post.';
      this.isRegistering = false;
      this.form.password = '';
      this.form.captchaAnswer = '';
    },

    async login() {
      const result = await apiService.login(this.form.email, this.form.password);

      if (result.login) {
        authService.setToken(result.login);
        this.$emit('loginSuccess');
      } else {
        throw new Error('Inloggning misslyckades');
      }
    },

    async resend() {
      this.loading = true;
      try {
        const r = await apiService.resendVerification(this.form.email);
        this.successMessage = r.message || 'Om kontot finns har en ny länk skickats.';
        this.error = null;
        this.showResend = false;
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.login-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.login-card {
  background: white;
  padding: 2.25rem;
  border-radius: 18px;
  box-shadow: 0 20px 48px rgba(16, 24, 40, 0.24);
  width: 90%;
  max-width: 410px;
  max-height: 90vh;
  overflow-y: auto;
}

.login-card h2 {
  text-align: center;
  margin-bottom: 1.5rem;
  color: #1f2933;
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.login-card h2::before {
  content: "🎁";
  display: block;
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.login-form {
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #555;
}

.form-group input {
  width: 100%;
  padding: 0.8rem 0.9rem;
  border: 1px solid #dfe3e8;
  border-radius: 10px;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #2e7d32;
  box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.15);
}

.form-group input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.captcha-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.5rem;
}

.captcha-img {
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  background: #f2f4f7;
  height: 54px;
  width: 160px;
}

.captcha-refresh {
  width: 40px;
  height: 40px;
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-size: 1.2rem;
  color: #2e7d32;
  transition: background 0.2s ease;
}

.captcha-refresh:hover:not(:disabled) {
  background: #e8f3e9;
}

.resend-link {
  display: block;
  margin: 0.5rem auto 0;
  background: none;
  border: none;
  color: #2e7d32;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
}

.error-message {
  background-color: #ffe6e6;
  color: #d63031;
  padding: 0.75rem;
  border-radius: 5px;
  margin-bottom: 1rem;
  text-align: center;
}

.success-message {
  background-color: #e6ffe6;
  color: #00b894;
  padding: 0.75rem;
  border-radius: 5px;
  margin-bottom: 1rem;
  text-align: center;
}

.submit-btn {
  width: 100%;
  padding: 0.85rem;
  background: linear-gradient(135deg, #2e7d32, #1b5e20);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(46, 125, 50, 0.28);
  transition: transform 0.15s ease, box-shadow 0.2s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(46, 125, 50, 0.38);
}

.submit-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.switch-mode {
  text-align: center;
  margin-bottom: 1rem;
}

.switch-mode p {
  margin: 0;
  color: #666;
}

.switch-link {
  color: #2e7d32;
  text-decoration: none;
  font-weight: 700;
}

.switch-link:hover {
  text-decoration: underline;
}

.close-btn {
  width: 100%;
  padding: 0.5rem;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.close-btn:hover:not(:disabled) {
  background-color: #5a6268;
}

.close-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>
